import React, { useState, useEffect } from 'react';
import { Search, Code, DataObject, ExpandLess, ExpandMore, ContentCopy, AccountTree, Cancel } from '@mui/icons-material';
import { Box, Paper, TextField, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Typography, Chip, InputAdornment, IconButton, Popover, Divider, Collapse, Tooltip, Alert } from '@mui/material';

const VariableReferenceHelper = ({ onInsert, anchorEl, onClose, nodeOutputs = {},currentValue = '' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState({});
  const [copiedPath, setCopiedPath] = useState('');

  const handleInsert = (variableRef) => {
    if (onInsert) onInsert(variableRef);
    onClose();
  }

  const handleCopy = (variableRef, event) => {
    event.stopPropagation();
    navigator.clipboard.writeText(variableRef);
    setCopiedPath(variableRef);
    setTimeout(() => setCopiedPath(''), 2000);
  }

  const toggleNodeExpansion = (nodeId) => setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));

  const renderPropertyItem = (nodeId, property, level = 0) => {
    const variableRef = `{{${nodeId}.${property.path}}}`;
    const displayPath = property.path.length > 30 ? '...' + property.path.slice(-27) : property.path;
    const typeIcons = { string: '"abc"', number: '123', boolean: 'bool', object: '{}', array: '[]' };
    const typeColors = { string: '#4caf50', number: '#2196f3', boolean: '#ff9800', object: '#9c27b0', array: '#f44336' };

    return (
      <ListItem 
        key={property.path}
        sx={{ pl: level * 2 + 2, py: 0.5 }}
        secondaryAction={
          <Tooltip title={copiedPath === variableRef ? 'Copied!' : 'Copy'}>
            <IconButton edge="end" size="small"onClick={(e) => handleCopy(variableRef, e)} >
              <ContentCopy sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        }
      >
        <ListItemButton onClick={() => handleInsert(variableRef)} sx={{ borderRadius: 1, py: 0.5, '&:hover': { backgroundColor: 'action.hover'} }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Chip 
              size="small"
              label={typeIcons[property.type] || property.type}
              sx={{ height: 20, color: 'white', fontSize: '0.7rem', '& .MuiChip-label': { px: 1 }, backgroundColor: typeColors[property.type] || '#666' }}
            />
          </ListItemIcon>
          <ListItemText 
            primary={ <Typography variant="body2" sx={{ fontFamily: 'monospace' }}> {displayPath} </Typography> }
            secondary={
              property.value !== undefined && typeof property.value !== 'object' ? (
                <Typography 
                  variant="caption" 
                  sx={{ color: 'text.secondary', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}
                >
                  {String(property.value)}
                </Typography>
              ) : null
            }
          />
        </ListItemButton>
      </ListItem>
    )
  }

  const filteredNodes = Object.entries(nodeOutputs).filter(([nodeId, data]) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    
    // Check node ID
    if (nodeId.toLowerCase().includes(searchLower)) return true;
    
    // Check node label if available
    if (data.label && data.label.toLowerCase().includes(searchLower)) return true;
    
    // Check properties
    if (data.properties)
      return data.properties.some(prop => 
        prop.path.toLowerCase().includes(searchLower) ||
        (typeof prop.value === 'string' && prop.value.toLowerCase().includes(searchLower))
      )

    return false;
  })

  return (
    <Popover
      onClose={onClose}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { width: 400, maxHeight: 500,display: 'flex',flexDirection: 'column' }}}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}> Insert Variable Reference </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={searchTerm}
          placeholder="Search nodes and properties..."
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"> <Search /> </InputAdornment>,
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Cancel sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>
      
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {filteredNodes.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No nodes with outputs available
            </Typography>
          </Box>
        ) : (
          <List dense>
            {filteredNodes.map(([nodeId, data]) => (
              <Box key={nodeId}>
                <ListItem
                  secondaryAction={
                    <IconButton size="small" onClick={() => toggleNodeExpansion(nodeId)} >
                      {expandedNodes[nodeId] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  }
                >
                  <ListItemButton onClick={() => handleInsert(`{{${nodeId}}}`)} sx={{ borderRadius: 1 }}>
                    <ListItemIcon>
                      <AccountTree sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText 
                      secondary={`{{${nodeId}}}`}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {data.label || `Node ${nodeId}`}
                          </Typography>
                          <Chip label={nodeId} size="small" sx={{ height: 18 }}/>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Collapse in={expandedNodes[nodeId]}>
                  {data.properties && data.properties.length > 0 ?
                    <Box sx={{ pl: 2 }}>
                      {data.properties.map(property => renderPropertyItem(nodeId, property, 1) )}
                    </Box>
                  :
                    <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
                      Node output: {typeof data.output === 'object' 
                        ? JSON.stringify(data.output).slice(0, 50) + '...' 
                        : String(data.output)}
                    </Alert>
                  }
                </Collapse>
                <Divider />
              </Box>
            ))}
          </List>
        )}
      </Box>
      
      <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          Click to insert • <ContentCopy sx={{ fontSize: 12, verticalAlign: 'middle' }} /> to copy
        </Typography>
      </Box>
    </Popover>
  );
};

// Hook for easy integration with text fields
export const useVariableReferenceHelper = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [insertCallback, setInsertCallback] = useState(null);

  const openHelper = (event, onInsert) => {
    setAnchorEl(event.currentTarget);
    setInsertCallback(() => onInsert);
  }

  const closeHelper = () => {
    setAnchorEl(null);
    setInsertCallback(null);
  }

  const handleInsert = (variableRef) => {
    if (insertCallback) insertCallback(variableRef);
    closeHelper();
  }

  return { anchorEl, openHelper, closeHelper, handleInsert }
}

export default VariableReferenceHelper;